// src/multi-modal.ts
function copyMultiModalContent(item) {
  if (item.type === "text") {
    const result2 = { type: "text" };
    if (item.text !== undefined)
      result2.text = item.text;
    return result2;
  }
  const result = { type: "image" };
  if (item.url !== undefined)
    result.url = item.url;
  if (item.mimeType !== undefined)
    result.mimeType = item.mimeType;
  if (item.text !== undefined)
    result.text = item.text;
  return result;
}
function copyContent(content) {
  if (typeof content === "string") {
    return content;
  }
  return content.map(copyMultiModalContent);
}
// src/schemas.ts
import { z } from "zod";
var isPlainObject = (value) => {
  if (!value || typeof value !== "object")
    return false;
  const prototype = Reflect.getPrototypeOf(value);
  return prototype === Object.prototype || prototype === null;
};
function toMultiModalContent(value) {
  const result = { type: value.type };
  if (value.text !== undefined)
    result.text = value.text;
  if (value.url !== undefined)
    result.url = value.url;
  if (value.mimeType !== undefined)
    result.mimeType = value.mimeType;
  return result;
}
var jsonValueSchema = z.lazy(() => {
  const jsonObjectSchema = z.preprocess((value, ctx) => {
    if (!isPlainObject(value)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "expected a plain object"
      });
      return z.NEVER;
    }
    return value;
  }, z.record(z.string(), jsonValueSchema));
  return z.union([
    z.string(),
    z.number().refine((value) => Number.isFinite(value), {
      message: "expected a finite number"
    }),
    z.boolean(),
    z.null(),
    z.array(jsonValueSchema),
    jsonObjectSchema
  ]);
});
var multiModalContentSchema = z.discriminatedUnion("type", [
  z.object({
    type: z.literal("text"),
    text: z.string()
  }),
  z.object({
    type: z.literal("image"),
    url: z.string().url(),
    mimeType: z.string().optional(),
    text: z.string().optional()
  })
]).transform(toMultiModalContent);
var messageRoleSchema = z.enum([
  "user",
  "assistant",
  "system",
  "developer",
  "tool-use",
  "tool-result",
  "snapshot"
]);
var toolCallSchema = z.object({
  id: z.string(),
  name: z.string(),
  arguments: jsonValueSchema
}).strict();
var toolResultSchema = z.object({
  callId: z.string(),
  outcome: z.enum(["success", "error"]),
  content: jsonValueSchema
}).strict();
var tokenUsageSchema = z.object({
  prompt: z.number().int().min(0),
  completion: z.number().int().min(0),
  total: z.number().int().min(0)
});
var messageInputSchema = z.object({
  role: messageRoleSchema,
  content: z.union([z.string(), z.array(multiModalContentSchema)]),
  metadata: z.record(z.string(), jsonValueSchema).optional(),
  hidden: z.boolean().optional(),
  toolCall: toolCallSchema.optional(),
  toolResult: toolResultSchema.optional(),
  tokenUsage: tokenUsageSchema.optional(),
  goalCompleted: z.boolean().optional()
}).strict();
var messageSchema = z.object({
  id: z.string(),
  role: messageRoleSchema,
  content: z.union([z.string(), z.array(multiModalContentSchema)]),
  position: z.number().int().min(0),
  createdAt: z.string(),
  metadata: z.record(z.string(), jsonValueSchema),
  hidden: z.boolean(),
  toolCall: toolCallSchema.optional(),
  toolResult: toolResultSchema.optional(),
  tokenUsage: tokenUsageSchema.optional(),
  goalCompleted: z.boolean().optional()
}).strict();
var conversationStatusSchema = z.enum([
  "active",
  "archived",
  "deleted"
]);
var conversationShape = {
  schemaVersion: z.number().int().min(1),
  id: z.string(),
  title: z.string().optional(),
  status: conversationStatusSchema,
  metadata: z.record(z.string(), jsonValueSchema),
  ids: z.array(z.string()),
  messages: z.record(z.string(), messageSchema),
  createdAt: z.string(),
  updatedAt: z.string()
};
var conversationSchema = z.object(conversationShape).strict();
// src/utilities/content.ts
function toMultiModalArray(input) {
  if (typeof input === "string")
    return [{ type: "text", text: input }];
  return Array.isArray(input) ? input : [input];
}
function normalizeContent(content) {
  if (content === undefined)
    return;
  if (typeof content === "string")
    return content;
  return Array.isArray(content) ? content : [content];
}
// src/utilities/type-helpers.ts
function toReadonly(value) {
  return value;
}

// src/utilities/message.ts
function createMessage(props) {
  const content = typeof props.content === "string" ? props.content : toReadonly(props.content.map((part) => part));
  const message = {
    id: props.id,
    role: props.role,
    content,
    position: props.position,
    createdAt: props.createdAt,
    metadata: toReadonly({ ...props.metadata }),
    hidden: props.hidden,
    toolCall: props.toolCall ? toReadonly({ ...props.toolCall }) : undefined,
    toolResult: props.toolResult ? toReadonly({ ...props.toolResult }) : undefined,
    tokenUsage: props.tokenUsage ? toReadonly({ ...props.tokenUsage }) : undefined
  };
  if (isAssistantMessage(props)) {
    return toReadonly({
      ...message,
      role: "assistant",
      goalCompleted: props.goalCompleted
    });
  }
  return toReadonly(message);
}
function messageParts(message) {
  if (typeof message.content === "string") {
    return message.content ? [{ type: "text", text: message.content }] : [];
  }
  return message.content;
}
function messageText(message, joiner = `

`) {
  if (typeof message.content === "string")
    return message.content;
  return messageParts(message).filter((p) => p.type === "text").map((p) => p.type === "text" ? p.text : "").join(joiner);
}
function messageHasImages(message) {
  return messageParts(message).some((p) => p.type === "image");
}
function isAssistantMessage(message) {
  return message.role === "assistant";
}
// src/utilities/tool-calls.ts
function pairToolCallsWithResults(messages) {
  const isMessageArray = (value) => Array.isArray(value);
  const ordered = isMessageArray(messages) ? messages : Object.values(messages).sort((a, b) => a.position - b.position);
  const pairs = [];
  const resultsMap = new Map;
  for (const msg of ordered) {
    if (msg.toolResult) {
      resultsMap.set(msg.toolResult.callId, msg.toolResult);
    }
  }
  for (const msg of ordered) {
    if (msg.toolCall) {
      pairs.push({
        call: msg.toolCall,
        result: resultsMap.get(msg.toolCall.id)
      });
    }
  }
  return pairs;
}
// src/errors.ts
class ConversationalistError extends Error {
  code;
  context;
  cause;
  constructor(code, message, options) {
    super(message);
    this.name = "ConversationalistError";
    this.code = code;
    this.context = options?.context;
    this.cause = options?.cause;
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, ConversationalistError);
    }
  }
  toDetailedString() {
    const parts = [`[${this.code}] ${this.message}`];
    if (this.context && Object.keys(this.context).length > 0) {
      parts.push(`Context: ${JSON.stringify(this.context, null, 2)}`);
    }
    if (this.cause) {
      parts.push(`Caused by: ${this.cause.message}`);
    }
    return parts.join(`
`);
  }
}
function createLockedError(conversationId) {
  return new ConversationalistError("error:locked", `conversation ${conversationId} is locked (concurrent modification detected)`, { context: { conversationId } });
}
function createInvalidInputError(message, context) {
  return new ConversationalistError("error:invalid-input", message, { context });
}
function createInvalidPositionError(expected, actual) {
  return new ConversationalistError("error:invalid-position", `invalid position: expected ${expected}, got ${actual}`, { context: { expected, actual } });
}
function createInvalidToolReferenceError(callId) {
  return new ConversationalistError("error:invalid-tool-reference", `tool result references non-existent tool-use: ${callId}`, { context: { callId } });
}
function createDuplicateIdError(id) {
  return new ConversationalistError("error:duplicate-id", `conversation with id ${id} already exists`, { context: { id } });
}
function createNotFoundError(id) {
  return new ConversationalistError("error:not-found", `conversation with id ${id} not found`, {
    context: { id }
  });
}
function createSerializationError(message, cause) {
  return new ConversationalistError("error:serialization", message, { cause });
}
function createValidationError(message, context, cause) {
  return new ConversationalistError("error:validation", message, { context, cause });
}
function createIntegrityError(message, context) {
  return new ConversationalistError("error:integrity", message, { context });
}

// src/conversation/integrity.ts
function validateConversationIntegrity(conversation) {
  const issues = [];
  const seenIds = new Set;
  conversation.ids.forEach((id, index) => {
    if (seenIds.has(id)) {
      issues.push({
        code: "integrity:duplicate-message-id",
        message: `duplicate message id in ids: ${id}`,
        data: { id, position: index }
      });
    } else {
      seenIds.add(id);
    }
    if (!conversation.messages[id]) {
      issues.push({
        code: "integrity:missing-message",
        message: `missing message for id ${id}`,
        data: { id, position: index }
      });
    }
  });
  for (const id of Object.keys(conversation.messages)) {
    if (!seenIds.has(id)) {
      issues.push({
        code: "integrity:unlisted-message",
        message: `message ${id} is not listed in ids`,
        data: { id }
      });
    }
  }
  const toolUses = new Map;
  conversation.ids.forEach((id, index) => {
    const message = conversation.messages[id];
    if (!message)
      return;
    if (message.role === "tool-use" && message.toolCall) {
      if (toolUses.has(message.toolCall.id)) {
        issues.push({
          code: "integrity:duplicate-tool-call",
          message: `duplicate toolCall.id ${message.toolCall.id}`,
          data: { toolCallId: message.toolCall.id, messageId: message.id }
        });
      } else {
        toolUses.set(message.toolCall.id, { position: index, messageId: message.id });
      }
    }
  });
  conversation.ids.forEach((id, index) => {
    const message = conversation.messages[id];
    if (!message)
      return;
    if (message.role === "tool-result" && message.toolResult) {
      const toolUse = toolUses.get(message.toolResult.callId);
      if (!toolUse) {
        issues.push({
          code: "integrity:orphan-tool-result",
          message: `tool-result references missing tool-use ${message.toolResult.callId}`,
          data: { callId: message.toolResult.callId, messageId: message.id }
        });
      } else if (toolUse.position >= index) {
        issues.push({
          code: "integrity:tool-result-before-call",
          message: `tool-result ${message.toolResult.callId} occurs before tool-use`,
          data: {
            callId: message.toolResult.callId,
            messageId: message.id,
            toolUseMessageId: toolUse.messageId
          }
        });
      }
    }
  });
  return issues;
}
function assertConversationIntegrity(conversation) {
  const issues = validateConversationIntegrity(conversation);
  if (issues.length === 0)
    return;
  throw createIntegrityError("conversation integrity check failed", { issues });
}

// src/conversation/validation.ts
function assertConversationSafe(conversation) {
  const parsed = conversationSchema.safeParse(conversation);
  if (!parsed.success) {
    throw createValidationError("conversation failed schema validation", {
      issues: parsed.error.issues
    });
  }
  assertConversationIntegrity(conversation);
}
function ensureConversationSafe(conversation) {
  assertConversationSafe(conversation);
  return conversation;
}

// src/utilities/message-store.ts
function getOrderedMessages(conversation) {
  const ordered = [];
  for (const id of conversation.ids) {
    const message = conversation.messages[id];
    if (message) {
      ordered.push(message);
    }
  }
  return ordered;
}
function toIdRecord(items) {
  const record = {};
  for (const item of items) {
    record[item.id] = item;
  }
  return record;
}

// src/utilities/transient.ts
function isTransientKey(key) {
  return key.startsWith("_");
}
function stripTransientFromRecord(metadata) {
  const result = {};
  for (const [key, value] of Object.entries(metadata)) {
    if (!isTransientKey(key)) {
      result[key] = value;
    }
  }
  return result;
}
function stripTransientMetadata(conversation) {
  const strippedMessages = getOrderedMessages(conversation).map((message) => {
    const baseMessage = {
      id: message.id,
      role: message.role,
      content: message.content,
      position: message.position,
      createdAt: message.createdAt,
      metadata: toReadonly(stripTransientFromRecord({ ...message.metadata })),
      hidden: message.hidden,
      toolCall: message.toolCall,
      toolResult: message.toolResult,
      tokenUsage: message.tokenUsage
    };
    if (isAssistantMessage(message)) {
      const assistantMessage = {
        ...baseMessage,
        role: "assistant",
        goalCompleted: message.goalCompleted
      };
      return toReadonly(assistantMessage);
    }
    return toReadonly(baseMessage);
  });
  return ensureConversationSafe(toReadonly({
    schemaVersion: conversation.schemaVersion,
    id: conversation.id,
    title: conversation.title,
    status: conversation.status,
    metadata: toReadonly(stripTransientFromRecord({ ...conversation.metadata })),
    ids: strippedMessages.map((message) => message.id),
    messages: toIdRecord(strippedMessages),
    createdAt: conversation.createdAt,
    updatedAt: conversation.updatedAt
  }));
}
// src/environment.ts
function simpleTokenEstimator(message) {
  const text = messageText(message);
  return Math.ceil(text.length / 4);
}
var defaultConversationEnvironment = {
  now: () => new Date().toISOString(),
  randomId: () => crypto.randomUUID(),
  estimateTokens: simpleTokenEstimator,
  plugins: []
};
function resolveConversationEnvironment(environment) {
  return {
    now: environment?.now ?? defaultConversationEnvironment.now,
    randomId: environment?.randomId ?? defaultConversationEnvironment.randomId,
    estimateTokens: environment?.estimateTokens ?? defaultConversationEnvironment.estimateTokens,
    plugins: [...environment?.plugins ?? defaultConversationEnvironment.plugins]
  };
}
function isConversationEnvironmentParameter(value) {
  if (!value || typeof value !== "object" || value === null)
    return false;
  if ("role" in value)
    return false;
  const candidate = value;
  return typeof candidate["now"] === "function" || typeof candidate["randomId"] === "function" || typeof candidate["estimateTokens"] === "function" || Array.isArray(candidate["plugins"]) && candidate["plugins"].length > 0;
}
function withEnvironment(environment, fn) {
  return (...args) => fn(...args, environment);
}

// src/types.ts
var CURRENT_SCHEMA_VERSION = 3;

// src/conversation/create.ts
function createConversation(options, environment) {
  const resolvedEnvironment = resolveConversationEnvironment(environment);
  const now = resolvedEnvironment.now();
  const conv = {
    schemaVersion: CURRENT_SCHEMA_VERSION,
    id: options?.id ?? resolvedEnvironment.randomId(),
    title: options?.title,
    status: options?.status ?? "active",
    metadata: { ...options?.metadata ?? {} },
    ids: [],
    messages: {},
    createdAt: now,
    updatedAt: now
  };
  return ensureConversationSafe(toReadonly(conv));
}
function createConversationUnsafe(options, environment) {
  const resolvedEnvironment = resolveConversationEnvironment(environment);
  const now = resolvedEnvironment.now();
  const conv = {
    schemaVersion: CURRENT_SCHEMA_VERSION,
    id: options?.id ?? resolvedEnvironment.randomId(),
    title: options?.title,
    status: options?.status ?? "active",
    metadata: { ...options?.metadata ?? {} },
    ids: [],
    messages: {},
    createdAt: now,
    updatedAt: now
  };
  return toReadonly(conv);
}
// src/conversation/tool-tracking.ts
var buildToolUseIndex = (messages) => messages.reduce((index, message) => {
  if (message.role === "tool-use" && message.toolCall) {
    index.set(message.toolCall.id, { name: message.toolCall.name });
  }
  return index;
}, new Map);
var registerToolUse = (index, toolCall) => {
  const next = new Map(index);
  next.set(toolCall.id, { name: toolCall.name });
  return next;
};
var assertToolReference = (index, callId) => {
  if (!index.has(callId)) {
    throw createInvalidToolReferenceError(callId);
  }
};

// src/conversation/append.ts
function partitionAppendArgs(args) {
  const filtered = args.filter((arg) => arg !== undefined);
  if (filtered.length === 0) {
    return { inputs: [] };
  }
  const last = filtered[filtered.length - 1];
  if (isConversationEnvironmentParameter(last)) {
    return {
      inputs: filtered.slice(0, -1),
      environment: last
    };
  }
  return { inputs: filtered };
}
function appendMessages(conversation, ...args) {
  return appendMessagesInternal(conversation, args, true);
}
function appendUnsafeMessage(conversation, input, environment) {
  return appendMessagesInternal(conversation, [input, environment], false);
}
var appendMessagesInternal = (conversation, args, validate) => {
  const { inputs, environment } = partitionAppendArgs(args);
  const resolvedEnvironment = resolveConversationEnvironment(environment);
  const now = resolvedEnvironment.now();
  const startPosition = conversation.ids.length;
  const initialToolUses = validate ? buildToolUseIndex(getOrderedMessages(conversation)) : new Map;
  const { messages } = inputs.reduce((state, input, index) => {
    const processedInput = resolvedEnvironment.plugins.reduce((acc, plugin) => plugin(acc), input);
    if (validate && processedInput.role === "tool-result" && processedInput.toolResult) {
      assertToolReference(state.toolUses, processedInput.toolResult.callId);
    }
    const normalizedContent = normalizeContent(processedInput.content);
    const baseMessage = {
      id: resolvedEnvironment.randomId(),
      role: processedInput.role,
      content: normalizedContent,
      position: startPosition + index,
      createdAt: now,
      metadata: { ...processedInput.metadata ?? {} },
      hidden: processedInput.hidden ?? false,
      toolCall: processedInput.toolCall,
      toolResult: processedInput.toolResult,
      tokenUsage: processedInput.tokenUsage
    };
    let message;
    if (processedInput.role === "assistant") {
      const assistantMessage = {
        ...baseMessage,
        role: "assistant",
        goalCompleted: processedInput.goalCompleted
      };
      message = createMessage(assistantMessage);
    } else {
      message = createMessage(baseMessage);
    }
    let toolUses = state.toolUses;
    if (processedInput.role === "tool-use" && processedInput.toolCall) {
      if (validate && state.toolUses.has(processedInput.toolCall.id)) {
        throw createIntegrityError("duplicate toolCall.id in conversation", {
          toolCallId: processedInput.toolCall.id,
          messageId: baseMessage.id
        });
      }
      toolUses = validate ? registerToolUse(state.toolUses, processedInput.toolCall) : state.toolUses;
    }
    return {
      toolUses,
      messages: [...state.messages, message]
    };
  }, { toolUses: initialToolUses, messages: [] });
  const messageIds = messages.map((message) => message.id);
  const next = {
    ...conversation,
    ids: [...conversation.ids, ...messageIds],
    messages: { ...conversation.messages, ...toIdRecord(messages) },
    updatedAt: now
  };
  const readonly = toReadonly(next);
  return validate ? ensureConversationSafe(readonly) : readonly;
};
function appendUserMessage(conversation, content, metadata, environment) {
  const resolvedEnvironment = isConversationEnvironmentParameter(metadata) ? metadata : environment;
  const resolvedMetadata = isConversationEnvironmentParameter(metadata) ? undefined : metadata;
  return appendMessages(conversation, { role: "user", content, metadata: resolvedMetadata }, resolvedEnvironment);
}
function appendAssistantMessage(conversation, content, metadata, environment) {
  const resolvedEnvironment = isConversationEnvironmentParameter(metadata) ? metadata : environment;
  const resolvedMetadata = isConversationEnvironmentParameter(metadata) ? undefined : metadata;
  return appendMessages(conversation, { role: "assistant", content, metadata: resolvedMetadata }, resolvedEnvironment);
}
function appendSystemMessage(conversation, content, metadata, environment) {
  const resolvedEnvironment = isConversationEnvironmentParameter(metadata) ? metadata : environment;
  const resolvedMetadata = isConversationEnvironmentParameter(metadata) ? undefined : metadata;
  return appendMessages(conversation, { role: "system", content, metadata: resolvedMetadata }, resolvedEnvironment);
}
// src/conversation/query.ts
function getMessages(conversation, options) {
  const includeHidden = options?.includeHidden ?? false;
  const ordered = getOrderedMessages(conversation);
  return includeHidden ? ordered : ordered.filter((m) => !m.hidden);
}
function getMessageAtPosition(conversation, position) {
  const id = conversation.ids[position];
  return id ? conversation.messages[id] : undefined;
}
function getMessageIds(conversation) {
  return [...conversation.ids];
}
function getMessageById(conversation, id) {
  return conversation.messages[id];
}
function searchConversationMessages(conversation, predicate) {
  return getOrderedMessages(conversation).filter(predicate);
}
function getStatistics(conversation) {
  const ordered = getOrderedMessages(conversation);
  const stats = ordered.reduce((acc, message) => {
    const byRole = {
      ...acc.byRole,
      [message.role]: (acc.byRole[message.role] ?? 0) + 1
    };
    return {
      byRole,
      hidden: acc.hidden + (message.hidden ? 1 : 0),
      withImages: acc.withImages + (messageHasImages(message) ? 1 : 0)
    };
  }, { byRole: {}, hidden: 0, withImages: 0 });
  return { total: ordered.length, ...stats };
}
// src/conversation/system-messages.ts
function hasSystemMessage(conversation) {
  return getOrderedMessages(conversation).some((m) => m.role === "system");
}
function getFirstSystemMessage(conversation) {
  return getOrderedMessages(conversation).find((m) => m.role === "system");
}
function getSystemMessages(conversation) {
  return getOrderedMessages(conversation).filter((m) => m.role === "system");
}
function prependSystemMessage(conversation, content, metadata, environment) {
  const resolvedEnvironment = resolveConversationEnvironment(isConversationEnvironmentParameter(metadata) ? metadata : environment);
  const resolvedMetadata = isConversationEnvironmentParameter(metadata) ? undefined : metadata;
  const now = resolvedEnvironment.now();
  const newMessage = createMessage({
    id: resolvedEnvironment.randomId(),
    role: "system",
    content,
    position: 0,
    createdAt: now,
    metadata: { ...resolvedMetadata ?? {} },
    hidden: false,
    toolCall: undefined,
    toolResult: undefined,
    tokenUsage: undefined
  });
  const ordered = getOrderedMessages(conversation);
  const renumberedMessages = ordered.map((message) => createMessage({
    id: message.id,
    role: message.role,
    content: copyContent(message.content),
    position: message.position + 1,
    createdAt: message.createdAt,
    metadata: { ...message.metadata },
    hidden: message.hidden,
    toolCall: message.toolCall,
    toolResult: message.toolResult,
    tokenUsage: message.tokenUsage
  }));
  return ensureConversationSafe(toReadonly({
    ...conversation,
    ids: [newMessage.id, ...ordered.map((message) => message.id)],
    messages: toIdRecord([newMessage, ...renumberedMessages]),
    updatedAt: now
  }));
}
function replaceSystemMessage(conversation, content, metadata, environment) {
  const resolvedEnvironment = resolveConversationEnvironment(isConversationEnvironmentParameter(metadata) ? metadata : environment);
  const resolvedMetadata = isConversationEnvironmentParameter(metadata) ? undefined : metadata;
  const now = resolvedEnvironment.now();
  const ordered = getOrderedMessages(conversation);
  const firstSystemIndex = ordered.findIndex((m) => m.role === "system");
  if (firstSystemIndex === -1) {
    return prependSystemMessage(conversation, content, resolvedMetadata, resolvedEnvironment);
  }
  const original = ordered[firstSystemIndex];
  const replaced = createMessage({
    id: original.id,
    role: "system",
    content,
    position: original.position,
    createdAt: original.createdAt,
    metadata: { ...resolvedMetadata ?? original.metadata },
    hidden: original.hidden,
    toolCall: undefined,
    toolResult: undefined,
    tokenUsage: undefined
  });
  const next = {
    ...conversation,
    ids: [...conversation.ids],
    messages: { ...conversation.messages, [replaced.id]: replaced },
    updatedAt: now
  };
  return ensureConversationSafe(toReadonly(next));
}
function collapseSystemMessages(conversation, environment) {
  const ordered = getOrderedMessages(conversation);
  const systemMessages = ordered.filter((m) => m.role === "system");
  if (systemMessages.length <= 1) {
    return ensureConversationSafe(conversation);
  }
  const resolvedEnvironment = resolveConversationEnvironment(environment);
  const now = resolvedEnvironment.now();
  const { parts } = systemMessages.reduce((state, message) => {
    const contentStr = typeof message.content === "string" ? message.content : message.content.map((part) => part.type === "text" ? part.text : "").join("");
    if (!contentStr || state.seen.has(contentStr)) {
      return state;
    }
    const seen = new Set(state.seen);
    seen.add(contentStr);
    return { seen, parts: [...state.parts, contentStr] };
  }, { seen: new Set, parts: [] });
  const collapsedContent = parts.join(`
`);
  const firstSystemMsg = systemMessages[0];
  const collapsed = createMessage({
    id: firstSystemMsg.id,
    role: "system",
    content: collapsedContent,
    position: firstSystemMsg.position,
    createdAt: firstSystemMsg.createdAt,
    metadata: { ...firstSystemMsg.metadata },
    hidden: firstSystemMsg.hidden,
    toolCall: undefined,
    toolResult: undefined,
    tokenUsage: undefined
  });
  const systemIdsToRemove = systemMessages.slice(1).reduce((ids, message) => {
    const nextIds = new Set(ids);
    nextIds.add(message.id);
    return nextIds;
  }, new Set);
  const messages = ordered.filter((m) => !systemIdsToRemove.has(m.id)).map((m) => m.id === firstSystemMsg.id ? collapsed : m);
  const renumbered = messages.map((message, index) => {
    if (message.position === index)
      return message;
    return createMessage({
      id: message.id,
      role: message.role,
      content: copyContent(message.content),
      position: index,
      createdAt: message.createdAt,
      metadata: { ...message.metadata },
      hidden: message.hidden,
      toolCall: message.toolCall,
      toolResult: message.toolResult,
      tokenUsage: message.tokenUsage
    });
  });
  const next = {
    ...conversation,
    ids: renumbered.map((message) => message.id),
    messages: toIdRecord(renumbered),
    updatedAt: now
  };
  return ensureConversationSafe(toReadonly(next));
}
// src/utilities/tool-results.ts
function redactToolResult(toolResult, placeholder) {
  return { ...toolResult, content: placeholder };
}

// src/conversation/modify.ts
var isRedactMessageOptions = (value) => {
  if (!value || typeof value !== "object")
    return false;
  const candidate = value;
  return "placeholder" in candidate || "redactToolArguments" in candidate || "redactToolResults" in candidate || "clearToolMetadata" in candidate;
};
function redactMessageAtPosition(conversation, position, placeholderOrOptions, environment) {
  let placeholder = "[REDACTED]";
  let options = {};
  let env = environment;
  if (typeof placeholderOrOptions === "string") {
    placeholder = placeholderOrOptions;
  } else if (placeholderOrOptions) {
    if (!environment && isConversationEnvironmentParameter(placeholderOrOptions)) {
      env = placeholderOrOptions;
    } else if (isRedactMessageOptions(placeholderOrOptions)) {
      options = placeholderOrOptions;
      if (options.placeholder) {
        placeholder = options.placeholder;
      }
    }
  }
  const redactToolArguments = options.redactToolArguments ?? true;
  const redactToolResults = options.redactToolResults ?? true;
  const clearToolMetadata = options.clearToolMetadata ?? false;
  if (position < 0 || position >= conversation.ids.length) {
    throw createInvalidPositionError(conversation.ids.length - 1, position);
  }
  const id = conversation.ids[position];
  const original = id ? conversation.messages[id] : undefined;
  if (!original) {
    throw createInvalidPositionError(conversation.ids.length - 1, position);
  }
  let toolCall = original.toolCall ? { ...original.toolCall } : undefined;
  let toolResult = original.toolResult ? { ...original.toolResult } : undefined;
  if (clearToolMetadata) {
    toolCall = undefined;
    toolResult = undefined;
  } else {
    if (original.role === "tool-use" && toolCall) {
      toolCall = {
        ...toolCall,
        arguments: redactToolArguments ? placeholder : toolCall.arguments
      };
    }
    if (original.role === "tool-result" && toolResult) {
      toolResult = redactToolResults ? redactToolResult(toolResult, placeholder) : { ...toolResult };
    }
  }
  const redacted = createMessage({
    id: original.id,
    role: original.role,
    content: placeholder,
    position: original.position,
    createdAt: original.createdAt,
    metadata: { ...original.metadata },
    hidden: original.hidden,
    toolCall,
    toolResult,
    tokenUsage: original.tokenUsage ? { ...original.tokenUsage } : undefined
  });
  const resolvedEnvironment = resolveConversationEnvironment(env);
  const now = resolvedEnvironment.now();
  const next = {
    ...conversation,
    ids: [...conversation.ids],
    messages: { ...conversation.messages, [redacted.id]: redacted },
    updatedAt: now
  };
  return ensureConversationSafe(toReadonly(next));
}
// src/conversation/serialization.ts
function normalizeToolResult(toolResult) {
  if (!toolResult)
    return;
  return {
    callId: toolResult.callId,
    outcome: toolResult.outcome,
    content: toolResult.content
  };
}
function normalizeMessage(message) {
  const base = {
    id: message.id,
    role: message.role,
    content: message.content,
    position: message.position,
    createdAt: message.createdAt,
    metadata: message.metadata,
    hidden: message.hidden,
    toolCall: message.toolCall ? { ...message.toolCall } : undefined,
    toolResult: normalizeToolResult(message.toolResult),
    tokenUsage: message.tokenUsage ? { ...message.tokenUsage } : undefined
  };
  if (isAssistantMessage(message)) {
    return {
      ...base,
      role: "assistant",
      goalCompleted: message.goalCompleted
    };
  }
  return base;
}
function deserializeConversation(json) {
  const parsed = conversationSchema.safeParse(json);
  if (!parsed.success) {
    throw createSerializationError("failed to deserialize conversation: invalid data");
  }
  const data = parsed.data;
  try {
    const messageIds = new Set(Object.keys(data.messages));
    const orderedMessages = data.ids.map((id, index) => {
      const message = data.messages[id];
      if (!message) {
        throw createSerializationError(`missing message for id ${id}`);
      }
      if (message.position !== index) {
        throw createInvalidPositionError(index, message.position);
      }
      messageIds.delete(id);
      return normalizeMessage(message);
    });
    if (messageIds.size > 0) {
      throw createSerializationError(`messages not listed in ids: ${[...messageIds].join(", ")}`);
    }
    orderedMessages.reduce((state, message) => {
      if (message.role === "tool-use" && message.toolCall) {
        return {
          toolUses: registerToolUse(state.toolUses, message.toolCall)
        };
      }
      if (message.role === "tool-result" && message.toolResult) {
        assertToolReference(state.toolUses, message.toolResult.callId);
      }
      return state;
    }, { toolUses: new Map });
    const messageInstances = orderedMessages.map((message) => createMessage(message));
    const conv = {
      schemaVersion: data.schemaVersion,
      id: data.id,
      title: data.title,
      status: data.status,
      metadata: { ...data.metadata },
      ids: orderedMessages.map((message) => message.id),
      messages: toIdRecord(messageInstances),
      createdAt: data.createdAt,
      updatedAt: data.updatedAt
    };
    const readonly = toReadonly(conv);
    assertConversationIntegrity(readonly);
    return readonly;
  } catch (error) {
    throw createSerializationError(`failed to deserialize conversation: ${error instanceof Error ? error.message : String(error)}`, error);
  }
}
// src/conversation/transform.ts
function toChatMessages(conversation) {
  assertConversationSafe(conversation);
  const roleMap = {
    user: "user",
    assistant: "assistant",
    system: "system",
    developer: "system",
    "tool-use": "assistant",
    "tool-result": "user",
    snapshot: "system"
  };
  const result = [];
  for (const message of getOrderedMessages(conversation)) {
    if (message.hidden)
      continue;
    const externalRole = roleMap[message.role];
    result.push({
      role: externalRole,
      content: message.content
    });
  }
  return result;
}
// src/conversation/tool-interactions.ts
function appendToolUse(conversation, toolCall, options, environment) {
  const resolved = resolveConversationEnvironment(isConversationEnvironmentParameter(options) ? options : environment);
  const resolvedOptions = isConversationEnvironmentParameter(options) ? undefined : options;
  const callId = toolCall.callId ?? resolved.randomId();
  const toolCallMeta = {
    id: callId,
    name: toolCall.toolId,
    arguments: toolCall.args
  };
  return appendMessages(conversation, {
    role: "tool-use",
    content: resolvedOptions?.content ?? "",
    metadata: resolvedOptions?.metadata,
    hidden: resolvedOptions?.hidden,
    toolCall: toolCallMeta,
    tokenUsage: resolvedOptions?.tokenUsage
  }, resolved);
}
function appendToolResult(conversation, toolResult, options, environment) {
  const resolvedOptions = isConversationEnvironmentParameter(options) ? undefined : options;
  const toolResultMeta = {
    callId: toolResult.callId,
    outcome: toolResult.outcome,
    content: toolResult.result
  };
  return appendMessages(conversation, {
    role: "tool-result",
    content: resolvedOptions?.content ?? "",
    metadata: resolvedOptions?.metadata,
    hidden: resolvedOptions?.hidden,
    toolResult: toolResultMeta,
    tokenUsage: resolvedOptions?.tokenUsage
  }, isConversationEnvironmentParameter(options) ? options : environment);
}
function getPendingToolCalls(conversation) {
  const ordered = getOrderedMessages(conversation);
  const completed = new Set;
  for (const message of ordered) {
    if (message.role === "tool-result" && message.toolResult) {
      completed.add(message.toolResult.callId);
    }
  }
  const pending = [];
  for (const message of ordered) {
    if (message.role === "tool-use" && message.toolCall) {
      if (!completed.has(message.toolCall.id)) {
        pending.push(message.toolCall);
      }
    }
  }
  return pending;
}
function getToolInteractions(conversation) {
  return pairToolCallsWithResults(getOrderedMessages(conversation));
}
// src/guards.ts
function isSchema(schema, value) {
  return schema.safeParse(value).success;
}
function isConversation(value) {
  return isSchema(conversationSchema, value);
}
function isConversationStatus(value) {
  return isSchema(conversationStatusSchema, value);
}
function isJSONValue(value) {
  return isSchema(jsonValueSchema, value);
}
function isMessage(value) {
  return isSchema(messageSchema, value);
}
function isMessageInput(value) {
  return isSchema(messageInputSchema, value);
}
function isMessageRole(value) {
  return isSchema(messageRoleSchema, value);
}
function isMultiModalContent(value) {
  return isSchema(multiModalContentSchema, value);
}
function isTokenUsage(value) {
  return isSchema(tokenUsageSchema, value);
}
function isToolCall(value) {
  return isSchema(toolCallSchema, value);
}
function isToolResult(value) {
  return isSchema(toolResultSchema, value);
}
// src/context.ts
var cloneMessageWithPosition = (message, position, content) => {
  const baseMessage = {
    id: message.id,
    role: message.role,
    content,
    position,
    createdAt: message.createdAt,
    metadata: { ...message.metadata },
    hidden: message.hidden,
    toolCall: message.toolCall ? { ...message.toolCall } : undefined,
    toolResult: message.toolResult ? { ...message.toolResult } : undefined,
    tokenUsage: message.tokenUsage ? { ...message.tokenUsage } : undefined
  };
  if (isAssistantMessage(message)) {
    const assistantMessage = {
      ...baseMessage,
      role: "assistant",
      goalCompleted: message.goalCompleted
    };
    return createMessage(assistantMessage);
  }
  return createMessage(baseMessage);
};
var createMessageBlock = (message, estimator) => ({
  messages: [message],
  minPosition: message.position,
  maxPosition: message.position,
  tokenCount: estimator(message)
});
var buildMessageBlocks = (messages, estimator, preserveToolPairs) => {
  if (!preserveToolPairs) {
    const blocks2 = messages.map((message) => createMessageBlock(message, estimator));
    const messageToBlock2 = new Map;
    for (const block of blocks2) {
      const message = block.messages[0];
      if (message) {
        messageToBlock2.set(message.id, block);
      }
    }
    return { blocks: blocks2, messageToBlock: messageToBlock2 };
  }
  const blocks = [];
  const toolUses = new Map;
  for (const message of messages) {
    if (message.role === "tool-use" && message.toolCall) {
      const block = createMessageBlock(message, estimator);
      toolUses.set(message.toolCall.id, block);
      blocks.push(block);
      continue;
    }
    if (message.role === "tool-result" && message.toolResult) {
      const existing = toolUses.get(message.toolResult.callId);
      if (existing) {
        existing.messages.push(message);
        existing.maxPosition = Math.max(existing.maxPosition, message.position);
        existing.tokenCount += estimator(message);
        continue;
      }
      const orphanBlock = createMessageBlock(message, estimator);
      orphanBlock.orphanToolResult = true;
      blocks.push(orphanBlock);
      continue;
    }
    blocks.push(createMessageBlock(message, estimator));
  }
  const filteredBlocks = blocks.filter((block) => !block.orphanToolResult);
  const messageToBlock = new Map;
  for (const block of filteredBlocks) {
    for (const message of block.messages) {
      messageToBlock.set(message.id, block);
    }
  }
  return { blocks: filteredBlocks, messageToBlock };
};
var collectBlocksForMessages = (messages, messageToBlock) => {
  const blocks = [];
  const seen = new Set;
  for (const message of messages) {
    const block = messageToBlock.get(message.id);
    if (block && !seen.has(block)) {
      seen.add(block);
      blocks.push(block);
    }
  }
  return blocks;
};
var collectMessagesFromBlocks = (blocks) => {
  const messages = [];
  const seen = new Set;
  for (const block of blocks) {
    for (const message of block.messages) {
      if (!seen.has(message.id)) {
        seen.add(message.id);
        messages.push(message);
      }
    }
  }
  messages.sort((a, b) => a.position - b.position);
  return messages;
};
var ensureTruncationSafe = (conversation, preserveToolPairs, operation) => {
  try {
    return ensureConversationSafe(conversation);
  } catch (error) {
    if (!preserveToolPairs && error instanceof ConversationalistError && error.code === "error:integrity")
      throw createIntegrityError(`${operation} produced invalid tool linkage; use preserveToolPairs: true to keep tool interactions intact`, { preserveToolPairs, issues: error.context?.["issues"] });
    throw error;
  }
};
function estimateConversationTokens(conversation, estimateTokens, environment) {
  let estimator = estimateTokens;
  let env = environment;
  if (!environment && estimateTokens && isConversationEnvironmentParameter(estimateTokens)) {
    env = estimateTokens;
    estimator = undefined;
  }
  const resolvedEnvironment = resolveConversationEnvironment(env);
  const finalEstimator = typeof estimator === "function" ? estimator : resolvedEnvironment.estimateTokens;
  return getOrderedMessages(conversation).reduce((total, message) => total + finalEstimator(message), 0);
}
function truncateToTokenLimit(conversation, maxTokens, optionsOrEstimator, environment) {
  assertConversationSafe(conversation);
  let options = {};
  let env = environment;
  if (typeof optionsOrEstimator === "function") {
    options = { estimateTokens: optionsOrEstimator };
  } else if (optionsOrEstimator) {
    if (!environment && isConversationEnvironmentParameter(optionsOrEstimator)) {
      const candidate = optionsOrEstimator;
      const hasEnvFields = !!(candidate["now"] || candidate["randomId"] || Array.isArray(candidate["plugins"]) && candidate["plugins"].length > 0);
      if (hasEnvFields) {
        env = optionsOrEstimator;
      } else {
        options = optionsOrEstimator;
      }
    } else {
      options = optionsOrEstimator;
    }
  }
  const resolvedEnvironment = resolveConversationEnvironment(env);
  const estimator = options.estimateTokens ?? resolvedEnvironment.estimateTokens;
  const preserveSystem = options.preserveSystemMessages ?? true;
  const preserveLastN = options.preserveLastN ?? 0;
  const preserveToolPairs = options.preserveToolPairs ?? true;
  const currentTokens = estimateConversationTokens(conversation, estimator, resolvedEnvironment);
  if (currentTokens <= maxTokens) {
    return conversation;
  }
  const now = resolvedEnvironment.now();
  const orderedMessages = getOrderedMessages(conversation);
  const { blocks, messageToBlock } = buildMessageBlocks(orderedMessages, estimator, preserveToolPairs);
  const systemMessages = preserveSystem ? orderedMessages.filter((m) => m.role === "system") : [];
  const nonSystemMessages = orderedMessages.filter((m) => m.role !== "system");
  const protectedMessages = preserveLastN > 0 ? nonSystemMessages.slice(-preserveLastN) : [];
  const systemBlocks = collectBlocksForMessages(systemMessages, messageToBlock);
  const protectedBlocks = collectBlocksForMessages(protectedMessages, messageToBlock);
  const lockedBlocks = new Set([...systemBlocks, ...protectedBlocks]);
  const removableBlocks = blocks.filter((block) => !lockedBlocks.has(block));
  const systemTokens = systemBlocks.reduce((sum, block) => sum + block.tokenCount, 0);
  const protectedTokens = protectedBlocks.reduce((sum, block) => sum + block.tokenCount, 0);
  const availableTokens = maxTokens - systemTokens - protectedTokens;
  let selectedBlocks = [];
  if (availableTokens <= 0) {
    selectedBlocks = [...systemBlocks, ...protectedBlocks];
  } else {
    const sortedRemovable = [...removableBlocks].sort((a, b) => a.maxPosition - b.maxPosition);
    const keptRemovable = [];
    let usedTokens = 0;
    for (let i = sortedRemovable.length - 1;i >= 0; i--) {
      const block = sortedRemovable[i];
      if (usedTokens + block.tokenCount <= availableTokens) {
        keptRemovable.unshift(block);
        usedTokens += block.tokenCount;
      } else {
        break;
      }
    }
    selectedBlocks = [...systemBlocks, ...keptRemovable, ...protectedBlocks];
  }
  const allMessages = collectMessagesFromBlocks(selectedBlocks);
  const renumbered = allMessages.map((message, index) => cloneMessageWithPosition(message, index, copyContent(message.content)));
  const next = toReadonly({
    ...conversation,
    ids: renumbered.map((message) => message.id),
    messages: toIdRecord(renumbered),
    updatedAt: now
  });
  return ensureTruncationSafe(next, preserveToolPairs, "truncateToTokenLimit");
}
function getRecentMessages(conversation, count, options) {
  const includeHidden = options?.includeHidden ?? false;
  const includeSystem = options?.includeSystem ?? false;
  const preserveToolPairs = options?.preserveToolPairs ?? true;
  const filtered = getOrderedMessages(conversation).filter((m) => {
    if (!includeHidden && m.hidden)
      return false;
    if (!includeSystem && m.role === "system")
      return false;
    return true;
  });
  if (!preserveToolPairs) {
    return filtered.slice(-count);
  }
  const { messageToBlock } = buildMessageBlocks(filtered, () => 0, preserveToolPairs);
  const tail = filtered.slice(-count);
  const blocks = collectBlocksForMessages(tail, messageToBlock);
  return collectMessagesFromBlocks(blocks);
}
function truncateFromPosition(conversation, position, options, environment) {
  assertConversationSafe(conversation);
  const preserveSystem = options?.preserveSystemMessages ?? true;
  const preserveToolPairs = options?.preserveToolPairs ?? true;
  const resolvedEnvironment = resolveConversationEnvironment(environment);
  const now = resolvedEnvironment.now();
  const ordered = getOrderedMessages(conversation);
  const { messageToBlock } = buildMessageBlocks(ordered, () => 0, preserveToolPairs);
  const systemMessages = preserveSystem ? ordered.filter((m) => m.role === "system" && m.position < position) : [];
  const keptMessages = ordered.filter((m) => m.position >= position);
  const systemBlocks = collectBlocksForMessages(systemMessages, messageToBlock);
  const keptBlocks = collectBlocksForMessages(keptMessages, messageToBlock);
  const allMessages = collectMessagesFromBlocks([...systemBlocks, ...keptBlocks]);
  const renumbered = allMessages.map((message, index) => cloneMessageWithPosition(message, index, copyContent(message.content)));
  const next = toReadonly({
    ...conversation,
    ids: renumbered.map((message) => message.id),
    messages: toIdRecord(renumbered),
    updatedAt: now
  });
  return ensureTruncationSafe(next, preserveToolPairs, "truncateFromPosition");
}

// src/streaming.ts
var STREAMING_KEY = "__streaming";
var cloneMessage = (original, overrides = {}) => {
  const baseMessage = {
    id: original.id,
    role: original.role,
    content: overrides.content ?? (typeof original.content === "string" ? original.content : [...original.content]),
    position: overrides.position ?? original.position,
    createdAt: original.createdAt,
    metadata: overrides.metadata ?? { ...original.metadata },
    hidden: original.hidden,
    toolCall: original.toolCall ? { ...original.toolCall } : undefined,
    toolResult: original.toolResult ? { ...original.toolResult } : undefined,
    tokenUsage: overrides.tokenUsage
  };
  if (isAssistantMessage(original)) {
    const assistantMessage = {
      ...baseMessage,
      role: "assistant",
      goalCompleted: original.goalCompleted
    };
    return createMessage(assistantMessage);
  }
  return createMessage(baseMessage);
};
function isStreamingMessage(message) {
  return message.metadata[STREAMING_KEY] === true;
}
function getStreamingMessage(conversation) {
  return getOrderedMessages(conversation).find(isStreamingMessage);
}
function appendStreamingMessage(conversation, role, metadata, environment) {
  const resolvedEnvironment = resolveConversationEnvironment(isConversationEnvironmentParameter(metadata) ? metadata : environment);
  const resolvedMetadata = isConversationEnvironmentParameter(metadata) ? undefined : metadata;
  const now = resolvedEnvironment.now();
  const messageId = resolvedEnvironment.randomId();
  const newMessage = createMessage({
    id: messageId,
    role,
    content: "",
    position: conversation.ids.length,
    createdAt: now,
    metadata: { ...resolvedMetadata ?? {}, [STREAMING_KEY]: true },
    hidden: false,
    toolCall: undefined,
    toolResult: undefined,
    tokenUsage: undefined
  });
  const updatedConversation = toReadonly({
    ...conversation,
    ids: [...conversation.ids, messageId],
    messages: { ...conversation.messages, [messageId]: newMessage },
    updatedAt: now
  });
  return { conversation: ensureConversationSafe(updatedConversation), messageId };
}
function updateStreamingMessage(conversation, messageId, content, environment) {
  const resolvedEnvironment = resolveConversationEnvironment(environment);
  const now = resolvedEnvironment.now();
  const original = conversation.messages[messageId];
  if (!original) {
    return ensureConversationSafe(conversation);
  }
  const overrides = {
    content: typeof content === "string" ? content : [...content]
  };
  if (original.tokenUsage) {
    overrides.tokenUsage = { ...original.tokenUsage };
  }
  const updated = cloneMessage(original, overrides);
  return ensureConversationSafe(toReadonly({
    ...conversation,
    ids: [...conversation.ids],
    messages: { ...conversation.messages, [updated.id]: updated },
    updatedAt: now
  }));
}
function finalizeStreamingMessage(conversation, messageId, options, environment) {
  const resolvedEnvironment = resolveConversationEnvironment(isConversationEnvironmentParameter(options) ? options : environment);
  const resolvedOptions = isConversationEnvironmentParameter(options) ? undefined : options;
  const now = resolvedEnvironment.now();
  const original = conversation.messages[messageId];
  if (!original) {
    return ensureConversationSafe(conversation);
  }
  const { [STREAMING_KEY]: _, ...restMetadata } = original.metadata;
  const finalMetadata = {
    ...restMetadata,
    ...resolvedOptions?.metadata ?? {}
  };
  const finalizeOverrides = {
    metadata: finalMetadata
  };
  if (resolvedOptions?.tokenUsage) {
    finalizeOverrides.tokenUsage = { ...resolvedOptions.tokenUsage };
  }
  const updated = cloneMessage(original, finalizeOverrides);
  return ensureConversationSafe(toReadonly({
    ...conversation,
    ids: [...conversation.ids],
    messages: { ...conversation.messages, [updated.id]: updated },
    updatedAt: now
  }));
}
function cancelStreamingMessage(conversation, messageId, environment) {
  const resolvedEnvironment = resolveConversationEnvironment(environment);
  const now = resolvedEnvironment.now();
  if (!conversation.messages[messageId]) {
    return ensureConversationSafe(conversation);
  }
  const messages = getOrderedMessages(conversation).filter((m) => m.id !== messageId).map((message, index) => message.position === index ? message : (() => {
    const overrides = {
      position: index
    };
    if (message.tokenUsage) {
      overrides.tokenUsage = { ...message.tokenUsage };
    }
    return cloneMessage(message, overrides);
  })());
  return ensureConversationSafe(toReadonly({
    ...conversation,
    ids: messages.map((message) => message.id),
    messages: toIdRecord(messages),
    updatedAt: now
  }));
}

// src/with-conversation.ts
function createDraft(initial) {
  let current = initial;
  const draft = {
    get value() {
      return current;
    },
    appendMessages: (...inputs) => {
      current = appendMessages(current, ...inputs);
      return draft;
    },
    appendUserMessage: (content, metadata) => {
      current = appendUserMessage(current, content, metadata);
      return draft;
    },
    appendAssistantMessage: (content, metadata) => {
      current = appendAssistantMessage(current, content, metadata);
      return draft;
    },
    appendSystemMessage: (content, metadata) => {
      current = appendSystemMessage(current, content, metadata);
      return draft;
    },
    prependSystemMessage: (content, metadata) => {
      current = prependSystemMessage(current, content, metadata);
      return draft;
    },
    replaceSystemMessage: (content, metadata) => {
      current = replaceSystemMessage(current, content, metadata);
      return draft;
    },
    collapseSystemMessages: () => {
      current = collapseSystemMessages(current);
      return draft;
    },
    redactMessageAtPosition: (position, placeholderOrOptions) => {
      current = redactMessageAtPosition(current, position, placeholderOrOptions);
      return draft;
    },
    appendStreamingMessage: (role, metadata) => {
      const result = appendStreamingMessage(current, role, metadata);
      current = result.conversation;
      return { draft, messageId: result.messageId };
    },
    updateStreamingMessage: (messageId, content) => {
      current = updateStreamingMessage(current, messageId, content);
      return draft;
    },
    finalizeStreamingMessage: (messageId, options) => {
      current = finalizeStreamingMessage(current, messageId, options);
      return draft;
    },
    cancelStreamingMessage: (messageId) => {
      current = cancelStreamingMessage(current, messageId);
      return draft;
    },
    truncateFromPosition: (position, options) => {
      current = truncateFromPosition(current, position, options);
      return draft;
    },
    truncateToTokenLimit: (maxTokens, options) => {
      current = truncateToTokenLimit(current, maxTokens, options);
      return draft;
    }
  };
  return draft;
}
function withConversation(conversation, fn) {
  const draft = createDraft(ensureConversationSafe(conversation));
  const maybePromise = fn(draft);
  if (maybePromise && typeof maybePromise === "object" && typeof maybePromise.then === "function") {
    return maybePromise.then(() => ensureConversationSafe(draft.value));
  }
  return ensureConversationSafe(draft.value);
}
function pipeConversation(conversation, ...fns) {
  const result = fns.reduce((current, fn) => fn(current), conversation);
  return ensureConversationSafe(result);
}
// node_modules/event-emission/dist/index.js
var SymbolObservable = typeof Symbol === "function" && Symbol.observable || Symbol.for("@@observable");
if (typeof Symbol === "function") {
  Symbol.observable = SymbolObservable;
}
function getMethod(obj, key) {
  if (obj === null || obj === undefined)
    return;
  const value = obj[key];
  if (value == null)
    return;
  if (typeof value !== "function") {
    throw new TypeError(value + " is not a function");
  }
  return value;
}
function hostReportError(e) {
  if (typeof queueMicrotask === "function") {
    queueMicrotask(() => {
      throw e;
    });
  } else {
    setTimeout(() => {
      throw e;
    });
  }
}
function SubscriptionObserverImpl(subscription) {
  this._subscription = subscription;
}
SubscriptionObserverImpl.prototype = Object.create(Object.prototype);
Object.defineProperties(SubscriptionObserverImpl.prototype, {
  constructor: { value: Object, configurable: true, writable: true },
  closed: {
    get() {
      return this._subscription._closed;
    },
    configurable: true
  },
  next: {
    value: function next(value) {
      const subscription = this._subscription;
      if (subscription._closed)
        return;
      const observer = subscription._observer;
      try {
        const m = getMethod(observer, "next");
        if (!m)
          return;
        return m.call(observer, value);
      } catch (e) {
        try {
          this.error(e);
        } catch (err) {
          hostReportError(err);
        }
      }
    },
    configurable: true,
    writable: true
  },
  error: {
    value: function error(errorValue) {
      const subscription = this._subscription;
      if (subscription._closed)
        throw errorValue;
      subscription._closed = true;
      const observer = subscription._observer;
      try {
        const m = getMethod(observer, "error");
        if (m) {
          return m.call(observer, errorValue);
        }
        throw errorValue;
      } finally {
        subscription._cleanup();
      }
    },
    configurable: true,
    writable: true
  },
  complete: {
    value: function complete(value) {
      const subscription = this._subscription;
      if (subscription._closed)
        return;
      subscription._closed = true;
      const observer = subscription._observer;
      try {
        const m = getMethod(observer, "complete");
        if (m) {
          return m.call(observer, value);
        }
        return;
      } finally {
        subscription._cleanup();
      }
    },
    configurable: true,
    writable: true
  }
});
Object.defineProperty(SubscriptionObserverImpl.prototype.next, "length", { value: 1 });
Object.defineProperty(SubscriptionObserverImpl.prototype.error, "length", { value: 1 });
Object.defineProperty(SubscriptionObserverImpl.prototype.complete, "length", {
  value: 1
});
function Subscription(observer, subscriber) {
  this._observer = observer;
  this._cleanupFn = undefined;
  this._closed = false;
  const subscriptionObserver = new SubscriptionObserverImpl(this);
  try {
    const start = getMethod(observer, "start");
    if (start) {
      start.call(observer, this);
    }
  } catch (e) {
    hostReportError(e);
  }
  if (this._closed)
    return;
  try {
    const cleanup = subscriber(subscriptionObserver);
    if (cleanup != null) {
      if (typeof cleanup !== "function" && typeof cleanup.unsubscribe !== "function") {
        throw new TypeError(cleanup + " is not a function or a subscription");
      }
      this._cleanupFn = cleanup;
      if (this._closed) {
        this._cleanup();
      }
    }
  } catch (e) {
    subscriptionObserver.error(e);
  }
}
Subscription.prototype = Object.create(Object.prototype);
Object.defineProperties(Subscription.prototype, {
  constructor: { value: Object, configurable: true, writable: true },
  closed: {
    get() {
      return this._closed;
    },
    configurable: true
  },
  unsubscribe: {
    value: function unsubscribe() {
      if (this._closed)
        return;
      this._closed = true;
      this._cleanup();
    },
    configurable: true,
    writable: true
  },
  _cleanup: {
    value: function _cleanup() {
      const cleanup = this._cleanupFn;
      if (!cleanup)
        return;
      this._cleanupFn = undefined;
      try {
        if (typeof cleanup === "function") {
          cleanup();
        } else if (cleanup && typeof cleanup.unsubscribe === "function") {
          cleanup.unsubscribe();
        }
      } catch (e) {
        hostReportError(e);
      }
    },
    configurable: true,
    writable: true
  }
});

class Observable {
  _subscriber;
  constructor(subscriber) {
    if (typeof subscriber !== "function") {
      throw new TypeError("Observable initializer must be a function");
    }
    this._subscriber = subscriber;
  }
  subscribe(observerOrNext, error2, complete2) {
    let observer;
    if (typeof observerOrNext === "function") {
      observer = {
        next: observerOrNext,
        error: error2,
        complete: complete2
      };
    } else if (typeof observerOrNext !== "object" || observerOrNext === null) {
      throw new TypeError(observerOrNext + " is not an object");
    } else {
      observer = observerOrNext;
    }
    return new Subscription(observer, this._subscriber);
  }
  [SymbolObservable]() {
    return this;
  }
  static of(...items) {
    const C = typeof this === "function" ? this : Observable;
    return new C((observer) => {
      for (let i = 0;i < items.length; ++i) {
        observer.next(items[i]);
        if (observer.closed)
          return;
      }
      observer.complete();
    });
  }
  static from(x) {
    const C = typeof this === "function" ? this : Observable;
    if (x == null)
      throw new TypeError(x + " is not an object");
    const method = x[SymbolObservable];
    if (method != null) {
      if (typeof method !== "function") {
        throw new TypeError(method + " is not a function");
      }
      const observable = method.call(x);
      if (Object(observable) !== observable) {
        throw new TypeError(observable + " is not an object");
      }
      if (observable.constructor === C) {
        return observable;
      }
      return new C((observer) => observable.subscribe(observer));
    }
    if (Symbol.iterator in x) {
      return new C((observer) => {
        for (const item of x) {
          observer.next(item);
          if (observer.closed)
            return;
        }
        observer.complete();
      });
    }
    throw new TypeError(x + " is not observable");
  }
}
var PROXY_MARKER = Symbol.for("@lasercat/event-emission/proxy");
var ORIGINAL_TARGET = Symbol.for("@lasercat/event-emission/original");
var ARRAY_MUTATORS = new Set([
  "push",
  "pop",
  "shift",
  "unshift",
  "splice",
  "sort",
  "reverse",
  "fill",
  "copyWithin"
]);
function isProxyable(value) {
  return value !== null && typeof value === "object" && !isProxied(value) && !(value instanceof Date) && !(value instanceof RegExp) && !(value instanceof Map) && !(value instanceof Set) && !(value instanceof WeakMap) && !(value instanceof WeakSet) && !(value instanceof Promise) && !(value instanceof Error) && !(value instanceof ArrayBuffer) && !ArrayBuffer.isView(value);
}
function isProxied(value) {
  return typeof value === "object" && value !== null && value[PROXY_MARKER] === true;
}
function isArrayMutator(prop) {
  return typeof prop === "string" && ARRAY_MUTATORS.has(prop);
}
function cloneAlongPath(obj, path) {
  const isArray = Array.isArray(obj);
  const rootClone = isArray ? [...obj] : { ...obj };
  if (!path || isArray) {
    return rootClone;
  }
  const parts = path.split(".");
  const result = rootClone;
  let current = result;
  for (let i = 0;i < parts.length; i++) {
    const key = parts[i];
    const value = current[key];
    if (value !== null && typeof value === "object") {
      current[key] = Array.isArray(value) ? [...value] : { ...value };
      if (i < parts.length - 1) {
        current = current[key];
      }
    } else {
      break;
    }
  }
  return result;
}
function cloneForComparison(obj, strategy, changedPath, deepClone) {
  if (obj === null || typeof obj !== "object")
    return obj;
  switch (strategy) {
    case "shallow":
      return Array.isArray(obj) ? [...obj] : { ...obj };
    case "deep":
      if (deepClone) {
        return deepClone(obj);
      }
      if (typeof structuredClone !== "function") {
        throw new Error("structuredClone is not available in this runtime; provide observe.deepClone, or use cloneStrategy 'path' or 'shallow'.");
      }
      return structuredClone(obj);
    case "path":
      return cloneAlongPath(obj, changedPath);
    default:
      return obj;
  }
}
function computeArrayDiff(method, before, _after, args) {
  switch (method) {
    case "push":
      return { added: args };
    case "pop":
      return { removed: before.length > 0 ? [before[before.length - 1]] : [] };
    case "shift":
      return { removed: before.length > 0 ? [before[0]] : [] };
    case "unshift":
      return { added: args };
    case "splice": {
      const [start, deleteCount, ...items] = args;
      const actualStart = start < 0 ? Math.max(before.length + start, 0) : Math.min(start, before.length);
      const actualDeleteCount = Math.min(deleteCount ?? before.length - actualStart, before.length - actualStart);
      return {
        removed: before.slice(actualStart, actualStart + actualDeleteCount),
        added: items
      };
    }
    case "sort":
    case "reverse":
    case "fill":
    case "copyWithin":
      return {};
    default:
      return {};
  }
}
var proxyRegistry = new WeakMap;
function getContextRegistry(target) {
  let contextMap = proxyRegistry.get(target);
  if (!contextMap) {
    contextMap = new WeakMap;
    proxyRegistry.set(target, contextMap);
  }
  return contextMap;
}
function createArrayMethodInterceptor(array, method, path, context) {
  const original = array[method];
  return function(...args) {
    const previousState = cloneForComparison(context.originalRoot, context.options.cloneStrategy, path, context.options.deepClone);
    const previousItems = [...array];
    const result = original.apply(this, args);
    const { added, removed } = computeArrayDiff(method, previousItems, array, args);
    const methodEventPath = path ? `update:${path}.${method}` : `update:${method}`;
    const arrayEventPath = path ? `update:${path}` : "update:";
    context.eventTarget.dispatchEvent({
      type: methodEventPath,
      detail: {
        method,
        args,
        result,
        added,
        removed,
        current: context.originalRoot,
        previous: previousState
      }
    });
    if (path) {
      context.eventTarget.dispatchEvent({
        type: arrayEventPath,
        detail: {
          value: array,
          current: context.originalRoot,
          previous: previousState
        }
      });
    }
    context.eventTarget.dispatchEvent({
      type: "update",
      detail: {
        current: context.originalRoot,
        previous: previousState
      }
    });
    return result;
  };
}
function createObservableProxyInternal(target, path, context) {
  const contextRegistry = getContextRegistry(target);
  const existing = contextRegistry.get(context);
  if (existing) {
    return existing.proxy;
  }
  const proxy = new Proxy(target, {
    get(obj, prop, receiver) {
      if (prop === PROXY_MARKER)
        return true;
      if (prop === ORIGINAL_TARGET)
        return obj;
      if (typeof prop === "symbol") {
        return Reflect.get(obj, prop, receiver);
      }
      const value = Reflect.get(obj, prop, receiver);
      if (Array.isArray(obj) && isArrayMutator(prop)) {
        return createArrayMethodInterceptor(obj, prop, path, context);
      }
      if (context.options.deep && isProxyable(value)) {
        const nestedPath = path ? `${path}.${prop}` : prop;
        return createObservableProxyInternal(value, nestedPath, context);
      }
      return value;
    },
    set(obj, prop, value, receiver) {
      if (typeof prop === "symbol") {
        return Reflect.set(obj, prop, value, receiver);
      }
      const oldValue = Reflect.get(obj, prop, receiver);
      if (Object.is(oldValue, value)) {
        return true;
      }
      const propPath = path ? `${path}.${prop}` : prop;
      const previousState = cloneForComparison(context.originalRoot, context.options.cloneStrategy, propPath, context.options.deepClone);
      const success = Reflect.set(obj, prop, value, receiver);
      if (success) {
        context.eventTarget.dispatchEvent({
          type: `update:${propPath}`,
          detail: {
            value,
            current: context.originalRoot,
            previous: previousState
          }
        });
        context.eventTarget.dispatchEvent({
          type: "update",
          detail: {
            current: context.originalRoot,
            previous: previousState
          }
        });
      }
      return success;
    },
    deleteProperty(obj, prop) {
      if (typeof prop === "symbol") {
        return Reflect.deleteProperty(obj, prop);
      }
      const propPath = path ? `${path}.${String(prop)}` : String(prop);
      const previousState = cloneForComparison(context.originalRoot, context.options.cloneStrategy, propPath, context.options.deepClone);
      const success = Reflect.deleteProperty(obj, prop);
      if (success) {
        context.eventTarget.dispatchEvent({
          type: `update:${propPath}`,
          detail: {
            value: undefined,
            current: context.originalRoot,
            previous: previousState
          }
        });
        context.eventTarget.dispatchEvent({
          type: "update",
          detail: {
            current: context.originalRoot,
            previous: previousState
          }
        });
      }
      return success;
    }
  });
  contextRegistry.set(context, {
    proxy,
    path
  });
  return proxy;
}
function isEventTarget(obj) {
  return typeof obj === "object" && obj !== null && typeof obj.addEventListener === "function" && typeof obj.removeEventListener === "function" && typeof obj.dispatchEvent === "function";
}
function setupEventForwarding(source, target) {
  const handlers = new Map;
  const sourceAddEventListener = source.addEventListener.bind(source);
  const sourceRemoveEventListener = source.removeEventListener.bind(source);
  const forwardHandler = (type) => (event) => {
    const detail = event.detail ?? event;
    target.dispatchEvent({
      type,
      detail
    });
  };
  const originalAddEventListener = target.addEventListener.bind(target);
  const wrappedAddEventListener = (type, listener, options) => {
    if (!handlers.has(type) && type !== "update" && !type.startsWith("update:")) {
      const handler = forwardHandler(type);
      handlers.set(type, handler);
      sourceAddEventListener(type, handler);
    }
    return originalAddEventListener(type, listener, options);
  };
  target.addEventListener = wrappedAddEventListener;
  return () => {
    target.addEventListener = originalAddEventListener;
    for (const [type, handler] of handlers) {
      sourceRemoveEventListener(type, handler);
    }
    handlers.clear();
  };
}
function createObservableProxy(target, eventTarget, options) {
  const resolvedOptions = {
    deep: options?.deep ?? true,
    cloneStrategy: options?.cloneStrategy ?? "path",
    deepClone: options?.deepClone
  };
  const context = {
    eventTarget,
    originalRoot: target,
    options: resolvedOptions
  };
  const proxy = createObservableProxyInternal(target, "", context);
  if (isEventTarget(target)) {
    const cleanupForwarding = setupEventForwarding(target, eventTarget);
    const maybeComplete = eventTarget.complete;
    if (typeof maybeComplete === "function") {
      const originalComplete = maybeComplete.bind(eventTarget);
      let cleaned = false;
      eventTarget.complete = () => {
        if (!cleaned) {
          cleaned = true;
          cleanupForwarding();
        }
        return originalComplete();
      };
    }
  }
  return proxy;
}

class BufferOverflowError extends Error {
  constructor(eventType, bufferSize) {
    super(`Buffer overflow for event type "${eventType}" (max: ${bufferSize})`);
    this.name = "BufferOverflowError";
  }
}
function matchesWildcard(eventType, pattern) {
  if (pattern === "*")
    return true;
  return pattern.endsWith(":*") && eventType.startsWith(pattern.slice(0, -2) + ":");
}
var EVENT_STATE = Symbol("event-emission:event-state");
function createEventTarget(targetOrOpts, opts) {
  if (opts?.observe === true && targetOrOpts && typeof targetOrOpts === "object") {
    const target = targetOrOpts;
    const eventTarget = createEventTargetInternal({
      onListenerError: opts.onListenerError
    });
    const proxy = createObservableProxy(target, eventTarget, {
      deep: opts.deep,
      cloneStrategy: opts.cloneStrategy,
      deepClone: opts.deepClone
    });
    const methodNames = [
      "addEventListener",
      "removeEventListener",
      "dispatchEvent",
      "clear",
      "on",
      "once",
      "removeAllListeners",
      "pipe",
      "addWildcardListener",
      "removeWildcardListener",
      "complete",
      "subscribe",
      "toObservable",
      "events"
    ];
    for (const name of methodNames) {
      Object.defineProperty(proxy, name, {
        value: eventTarget[name],
        writable: false,
        enumerable: false,
        configurable: true
      });
    }
    Object.defineProperty(proxy, "completed", {
      get: () => eventTarget.completed,
      enumerable: false,
      configurable: true
    });
    return proxy;
  }
  return createEventTargetInternal(targetOrOpts);
}
function createEventTargetInternal(opts) {
  const listeners = new Map;
  const wildcardListeners = new Set;
  let isCompleted = false;
  const completionCallbacks = new Set;
  const now = () => typeof globalThis.performance?.now === "function" ? globalThis.performance.now() : Date.now();
  const initializeEventState = (state, type, bubbles, cancelable) => {
    state.initializedFlag = true;
    state.stopPropagationFlag = false;
    state.stopImmediatePropagationFlag = false;
    state.canceledFlag = false;
    state.isTrusted = false;
    state.target = null;
    state.currentTarget = null;
    state.eventPhase = 0;
    state.type = type;
    state.bubbles = bubbles;
    state.cancelable = cancelable;
  };
  const setCanceledFlag = (state) => {
    if (state.cancelable && !state.inPassiveListenerFlag) {
      state.canceledFlag = true;
    }
  };
  const createEvent = (type, detail, init) => {
    const state = {
      dispatchFlag: false,
      initializedFlag: true,
      stopPropagationFlag: false,
      stopImmediatePropagationFlag: false,
      canceledFlag: false,
      inPassiveListenerFlag: false,
      composedFlag: Boolean(init?.composed),
      eventPhase: init?.eventPhase ?? 0,
      currentTarget: init?.currentTarget ?? init?.target ?? null,
      target: init?.target ?? null,
      timeStamp: init?.timeStamp ?? now(),
      path: [],
      type,
      bubbles: Boolean(init?.bubbles),
      cancelable: Boolean(init?.cancelable),
      isTrusted: false
    };
    const event = { detail };
    Object.defineProperties(event, {
      type: {
        get: () => state.type,
        enumerable: true,
        configurable: true
      },
      bubbles: {
        get: () => state.bubbles,
        enumerable: true,
        configurable: true
      },
      cancelable: {
        get: () => state.cancelable,
        enumerable: true,
        configurable: true
      },
      cancelBubble: {
        get: () => state.stopPropagationFlag,
        set: (value) => {
          if (value)
            state.stopPropagationFlag = true;
        },
        enumerable: true,
        configurable: true
      },
      composed: {
        get: () => state.composedFlag,
        enumerable: true,
        configurable: true
      },
      currentTarget: {
        get: () => state.currentTarget,
        enumerable: true,
        configurable: true
      },
      defaultPrevented: {
        get: () => state.canceledFlag,
        enumerable: true,
        configurable: true
      },
      eventPhase: {
        get: () => state.eventPhase,
        enumerable: true,
        configurable: true
      },
      isTrusted: {
        get: () => state.isTrusted,
        enumerable: true,
        configurable: true
      },
      returnValue: {
        get: () => !state.canceledFlag,
        set: (value) => {
          if (value === false)
            setCanceledFlag(state);
        },
        enumerable: true,
        configurable: true
      },
      srcElement: {
        get: () => state.target,
        enumerable: true,
        configurable: true
      },
      target: {
        get: () => state.target,
        enumerable: true,
        configurable: true
      },
      timeStamp: {
        get: () => state.timeStamp,
        enumerable: true,
        configurable: true
      },
      composedPath: {
        value: () => state.path.map((entry) => entry.invocationTarget),
        enumerable: true,
        configurable: true
      },
      initEvent: {
        value: (newType, bubbles = false, cancelable = false) => {
          if (state.dispatchFlag)
            return;
          initializeEventState(state, newType, Boolean(bubbles), Boolean(cancelable));
        },
        enumerable: true,
        configurable: true
      },
      preventDefault: {
        value: () => setCanceledFlag(state),
        enumerable: true,
        configurable: true
      },
      stopImmediatePropagation: {
        value: () => {
          state.stopPropagationFlag = true;
          state.stopImmediatePropagationFlag = true;
        },
        enumerable: true,
        configurable: true
      },
      stopPropagation: {
        value: () => {
          state.stopPropagationFlag = true;
        },
        enumerable: true,
        configurable: true
      },
      NONE: { value: 0, enumerable: true, configurable: true },
      CAPTURING_PHASE: { value: 1, enumerable: true, configurable: true },
      AT_TARGET: { value: 2, enumerable: true, configurable: true },
      BUBBLING_PHASE: { value: 3, enumerable: true, configurable: true },
      [EVENT_STATE]: {
        value: state,
        enumerable: false,
        configurable: false
      }
    });
    return event;
  };
  const getEventState = (event) => event[EVENT_STATE];
  const normalizeAddListenerOptions = (options) => {
    if (typeof options === "boolean") {
      return {
        capture: options,
        passive: false,
        once: false,
        signal: null
      };
    }
    return {
      capture: Boolean(options?.capture),
      passive: Boolean(options?.passive),
      once: Boolean(options?.once),
      signal: options?.signal ?? null
    };
  };
  const normalizeCaptureOption = (options) => {
    if (typeof options === "boolean")
      return options;
    return Boolean(options?.capture);
  };
  const removeListenerRecord = (type, record) => {
    if (record.removed)
      return;
    record.removed = true;
    const list = listeners.get(type);
    if (list) {
      const idx = list.indexOf(record);
      if (idx >= 0)
        list.splice(idx, 1);
      if (list.length === 0)
        listeners.delete(type);
    }
    if (record.signal && record.abortHandler) {
      record.signal.removeEventListener("abort", record.abortHandler);
    }
  };
  const handleListenerError = (eventType, error2) => {
    if (eventType === "error")
      return;
    if (opts?.onListenerError) {
      opts.onListenerError(eventType, error2);
      return;
    }
    const errorListeners = listeners.get("error");
    if (errorListeners && errorListeners.length > 0) {
      dispatchEvent({ type: "error", detail: error2 });
    } else {
      throw error2;
    }
  };
  const notifyWildcardListeners = (eventType, event) => {
    if (wildcardListeners.size === 0)
      return;
    for (const rec of Array.from(wildcardListeners)) {
      if (!matchesWildcard(eventType, rec.pattern))
        continue;
      const baseEvent = createEvent(rec.pattern, event.detail, {
        target,
        currentTarget: target,
        eventPhase: 2,
        bubbles: event.bubbles,
        cancelable: event.cancelable,
        composed: event.composed
      });
      const wildcardEvent = Object.defineProperties(baseEvent, {
        originalType: { value: eventType, enumerable: true, configurable: true }
      });
      try {
        const fn = rec.fn;
        const res = fn(wildcardEvent);
        if (res && typeof res.then === "function") {
          res.catch((error2) => {
            try {
              handleListenerError(eventType, error2);
            } catch (rethrown) {
              queueMicrotask(() => {
                throw rethrown;
              });
            }
          });
        }
      } catch (error2) {
        handleListenerError(eventType, error2);
      } finally {
        if (rec.once)
          wildcardListeners.delete(rec);
      }
      const state = getEventState(wildcardEvent);
      if (state?.stopImmediatePropagationFlag || state?.stopPropagationFlag) {
        break;
      }
    }
  };
  const addEventListener = (type, listener, options) => {
    if (isCompleted || !listener) {
      return () => {};
    }
    const { capture, passive, once, signal } = normalizeAddListenerOptions(options);
    let list = listeners.get(type);
    if (!list) {
      list = [];
      listeners.set(type, list);
    }
    for (const existing of list) {
      if (existing.original === listener && existing.capture === capture) {
        return () => removeEventListener(type, listener, options);
      }
    }
    const original = listener;
    const callback = typeof listener === "function" ? listener : (event) => listener.handleEvent(event);
    const record = {
      type,
      original,
      callback,
      capture,
      passive,
      once,
      signal,
      removed: false
    };
    list.push(record);
    const unsubscribe2 = () => removeListenerRecord(type, record);
    if (signal) {
      const onAbort = () => removeListenerRecord(type, record);
      record.abortHandler = onAbort;
      signal.addEventListener("abort", onAbort, { once: true });
      if (signal.aborted)
        onAbort();
    }
    return unsubscribe2;
  };
  const addWildcardListener = (pattern, listener, options) => {
    if (isCompleted)
      return () => {};
    const opts2 = options ?? {};
    for (const existing of wildcardListeners) {
      if (existing.pattern === pattern && existing.fn === listener) {
        return () => removeWildcardListener(pattern, listener);
      }
    }
    const record = {
      fn: listener,
      pattern,
      once: opts2.once,
      signal: opts2.signal
    };
    wildcardListeners.add(record);
    const unsubscribe2 = () => {
      wildcardListeners.delete(record);
      if (record.signal && record.abortHandler) {
        record.signal.removeEventListener("abort", record.abortHandler);
      }
    };
    if (opts2.signal) {
      const onAbort = () => unsubscribe2();
      record.abortHandler = onAbort;
      opts2.signal.addEventListener("abort", onAbort, { once: true });
      if (opts2.signal.aborted)
        onAbort();
    }
    return unsubscribe2;
  };
  const removeWildcardListener = (pattern, listener) => {
    for (const record of Array.from(wildcardListeners)) {
      if (record.pattern === pattern && record.fn === listener) {
        wildcardListeners.delete(record);
        if (record.signal && record.abortHandler) {
          record.signal.removeEventListener("abort", record.abortHandler);
        }
      }
    }
  };
  const invokeListeners = (eventType, event, phase, listenersSnapshot) => {
    const state = getEventState(event);
    if (!state || state.stopPropagationFlag)
      return;
    state.currentTarget = target;
    state.target = target;
    state.eventPhase = event.AT_TARGET;
    for (const rec of listenersSnapshot) {
      if (rec.removed)
        continue;
      if (phase === "capturing" && !rec.capture)
        continue;
      if (phase === "bubbling" && rec.capture)
        continue;
      if (rec.once)
        removeListenerRecord(rec.type, rec);
      if (rec.passive)
        state.inPassiveListenerFlag = true;
      try {
        const res = rec.callback.call(state.currentTarget, event);
        if (res && typeof res.then === "function") {
          res.catch((error2) => {
            try {
              handleListenerError(eventType, error2);
            } catch (rethrown) {
              queueMicrotask(() => {
                throw rethrown;
              });
            }
          });
        }
      } catch (error2) {
        handleListenerError(eventType, error2);
      } finally {
        if (rec.passive)
          state.inPassiveListenerFlag = false;
      }
      if (state.stopImmediatePropagationFlag)
        break;
    }
  };
  const dispatchEvent = (eventInput) => {
    if (isCompleted)
      return false;
    let event;
    let state;
    if (eventInput && typeof eventInput === "object") {
      state = getEventState(eventInput);
      if (state) {
        event = eventInput;
      } else {
        const input = eventInput;
        if (typeof input.type !== "string") {
          throw new TypeError("Event type must be a string");
        }
        event = createEvent(input.type, input.detail, {
          bubbles: input.bubbles,
          cancelable: input.cancelable,
          composed: input.composed,
          timeStamp: input.timeStamp
        });
        state = getEventState(event);
      }
    } else {
      throw new TypeError("dispatchEvent expects an event object");
    }
    const dispatchState = state ?? getEventState(event);
    if (dispatchState.dispatchFlag || !dispatchState.initializedFlag) {
      const message = "Failed to execute dispatchEvent: event is already being dispatched";
      if (typeof globalThis.DOMException === "function") {
        throw new globalThis.DOMException(message, "InvalidStateError");
      }
      const err = new Error(message);
      err.name = "InvalidStateError";
      throw err;
    }
    dispatchState.isTrusted = false;
    dispatchState.dispatchFlag = true;
    dispatchState.path = [
      {
        invocationTarget: target,
        invocationTargetInShadowTree: false,
        shadowAdjustedTarget: target,
        relatedTarget: null,
        touchTargets: [],
        rootOfClosedTree: false,
        slotInClosedTree: false
      }
    ];
    notifyWildcardListeners(dispatchState.type, event);
    const list = listeners.get(dispatchState.type);
    const snapshot = list ? list.slice() : [];
    invokeListeners(dispatchState.type, event, "capturing", snapshot);
    invokeListeners(dispatchState.type, event, "bubbling", snapshot);
    dispatchState.eventPhase = event.NONE;
    dispatchState.currentTarget = null;
    dispatchState.path = [];
    dispatchState.dispatchFlag = false;
    dispatchState.stopPropagationFlag = false;
    dispatchState.stopImmediatePropagationFlag = false;
    return !dispatchState.canceledFlag;
  };
  const removeEventListener = (type, listener, options) => {
    if (!listener)
      return;
    const capture = normalizeCaptureOption(options);
    const list = listeners.get(type);
    if (!list)
      return;
    for (const record of [...list]) {
      if (record.original === listener && record.capture === capture) {
        removeListenerRecord(type, record);
      }
    }
  };
  const clear = () => {
    for (const [type, list] of Array.from(listeners.entries())) {
      for (const record of [...list]) {
        removeListenerRecord(type, record);
      }
    }
    listeners.clear();
    for (const record of wildcardListeners) {
      if (record.signal && record.abortHandler) {
        record.signal.removeEventListener("abort", record.abortHandler);
      }
    }
    wildcardListeners.clear();
  };
  const on = (type, options) => {
    return new Observable((observer) => {
      let opts2;
      if (typeof options === "boolean") {
        opts2 = { capture: options };
      } else {
        opts2 = options ?? {};
      }
      const handler = opts2.handler;
      const once = opts2.once;
      const eventHandler = (e) => {
        let success = false;
        try {
          if (handler) {
            handler(e);
          }
          observer.next(e);
          success = true;
        } finally {
          if (once && success) {
            observer.complete();
          }
        }
      };
      const errorHandler = (e) => {
        observer.error(e.detail);
      };
      const unsubscribeEvent = addEventListener(type, eventHandler, opts2);
      let unsubscribeError;
      if (opts2.receiveError) {
        unsubscribeError = addEventListener("error", errorHandler, opts2);
      }
      return () => {
        unsubscribeEvent();
        if (unsubscribeError) {
          unsubscribeError();
        }
      };
    });
  };
  const onceMethod = (type, listener, options) => {
    if (typeof options === "boolean") {
      return addEventListener(type, listener, { capture: options, once: true });
    }
    return addEventListener(type, listener, { ...options ?? {}, once: true });
  };
  const removeAllListeners = (type) => {
    if (type !== undefined) {
      const list = listeners.get(type);
      if (list) {
        for (const record of [...list]) {
          removeListenerRecord(type, record);
        }
        listeners.delete(type);
      }
    } else {
      for (const [eventType, list] of Array.from(listeners.entries())) {
        for (const record of [...list]) {
          removeListenerRecord(eventType, record);
        }
      }
      listeners.clear();
      for (const record of wildcardListeners) {
        if (record.signal && record.abortHandler) {
          record.signal.removeEventListener("abort", record.abortHandler);
        }
      }
      wildcardListeners.clear();
    }
  };
  const pipe = (target2, mapFn) => {
    if (isCompleted) {
      return () => {};
    }
    const unsubscribe2 = addWildcardListener("*", (event) => {
      if (mapFn) {
        const mapped = mapFn(createEvent(event.originalType, event.detail, {
          target: target2,
          currentTarget: target2,
          eventPhase: 2,
          bubbles: event.bubbles,
          cancelable: event.cancelable,
          composed: event.composed
        }));
        if (mapped !== null) {
          target2.dispatchEvent(mapped);
        }
      } else {
        target2.dispatchEvent({
          type: event.originalType,
          detail: event.detail
        });
      }
    });
    const completionUnsub = () => {
      unsubscribe2();
    };
    completionCallbacks.add(completionUnsub);
    return () => {
      completionCallbacks.delete(completionUnsub);
      unsubscribe2();
    };
  };
  const complete2 = () => {
    if (isCompleted)
      return;
    isCompleted = true;
    for (const cb of completionCallbacks) {
      try {
        cb();
      } catch (err) {
        try {
          handleListenerError("complete", err);
        } catch {}
      }
    }
    completionCallbacks.clear();
    for (const [eventType, list] of Array.from(listeners.entries())) {
      for (const record of [...list]) {
        removeListenerRecord(eventType, record);
      }
    }
    listeners.clear();
    for (const record of wildcardListeners) {
      if (record.signal && record.abortHandler) {
        record.signal.removeEventListener("abort", record.abortHandler);
      }
    }
    wildcardListeners.clear();
  };
  const subscribe = (type, observerOrNext, error2, completeHandler) => {
    let observer;
    if (typeof observerOrNext === "function") {
      observer = {
        next: observerOrNext,
        error: error2,
        complete: completeHandler
      };
    } else {
      observer = observerOrNext ?? {};
    }
    let closed = false;
    if (isCompleted) {
      if (observer.complete) {
        try {
          observer.complete();
        } catch {}
      }
      return {
        unsubscribe: () => {
          closed = true;
        },
        get closed() {
          return closed || isCompleted;
        }
      };
    }
    const unsub = addEventListener(type, (event) => {
      if (closed)
        return;
      if (observer.next) {
        try {
          observer.next(event);
        } catch (err) {
          if (observer.error) {
            try {
              observer.error(err);
            } catch {}
          }
        }
      }
    });
    const onComplete = () => {
      if (closed)
        return;
      closed = true;
      if (observer.complete) {
        try {
          observer.complete();
        } catch {}
      }
    };
    completionCallbacks.add(onComplete);
    return {
      unsubscribe: () => {
        if (closed)
          return;
        closed = true;
        completionCallbacks.delete(onComplete);
        unsub();
      },
      get closed() {
        return closed || isCompleted;
      }
    };
  };
  const toObservable = () => {
    return new Observable((observer) => {
      if (isCompleted) {
        observer.complete();
        return;
      }
      const wildcardListener = (event) => {
        observer.next(createEvent(event.originalType, event.detail, {
          target,
          currentTarget: target,
          eventPhase: 2,
          bubbles: event.bubbles,
          cancelable: event.cancelable,
          composed: event.composed
        }));
      };
      const unsubscribe2 = addWildcardListener("*", wildcardListener);
      const onComplete = () => {
        observer.complete();
      };
      completionCallbacks.add(onComplete);
      return () => {
        unsubscribe2();
        completionCallbacks.delete(onComplete);
      };
    });
  };
  function events(type, options) {
    if (isCompleted) {
      return {
        [Symbol.asyncIterator]() {
          return this;
        },
        next() {
          return Promise.resolve({
            value: undefined,
            done: true
          });
        },
        return() {
          return Promise.resolve({
            value: undefined,
            done: true
          });
        }
      };
    }
    const signal = options?.signal;
    const bufferSize = options?.bufferSize ?? Infinity;
    const overflowStrategy = options?.overflowStrategy ?? "drop-oldest";
    const buffer = [];
    let resolve = null;
    let done = false;
    let hasOverflow = false;
    let onAbort = null;
    const cleanupAbortListener = () => {
      if (signal && onAbort) {
        signal.removeEventListener("abort", onAbort);
      }
    };
    const unsub = addEventListener(type, (event) => {
      if (done)
        return;
      const typedEvent = event;
      if (resolve) {
        const r = resolve;
        resolve = null;
        r({ value: typedEvent, done: false });
      } else {
        if (buffer.length >= bufferSize && bufferSize !== Infinity) {
          switch (overflowStrategy) {
            case "drop-oldest":
              buffer.shift();
              buffer.push(typedEvent);
              break;
            case "drop-latest":
              break;
            case "throw":
              unsub();
              completionCallbacks.delete(onComplete);
              done = true;
              hasOverflow = true;
              cleanupAbortListener();
              return;
          }
        } else {
          buffer.push(typedEvent);
        }
      }
    });
    const onComplete = () => {
      done = true;
      cleanupAbortListener();
      if (resolve) {
        const r = resolve;
        resolve = null;
        r({ value: undefined, done: true });
      }
    };
    completionCallbacks.add(onComplete);
    if (signal) {
      onAbort = () => {
        done = true;
        completionCallbacks.delete(onComplete);
        unsub();
        if (resolve) {
          const r = resolve;
          resolve = null;
          r({ value: undefined, done: true });
        }
      };
      signal.addEventListener("abort", onAbort, { once: true });
      if (signal.aborted)
        onAbort();
    }
    const iterator = {
      [Symbol.asyncIterator]() {
        return this;
      },
      async next() {
        if (buffer.length > 0) {
          return { value: buffer.shift(), done: false };
        }
        if (resolve !== null) {
          return Promise.reject(new Error("Concurrent calls to next() are not supported on this async iterator"));
        }
        return new Promise((_resolve, _reject) => {
          if (hasOverflow) {
            hasOverflow = false;
            _reject(new BufferOverflowError(type, bufferSize));
            return;
          }
          if (done) {
            _resolve({
              value: undefined,
              done: true
            });
            return;
          }
          resolve = _resolve;
        });
      },
      return() {
        if (resolve) {
          const r = resolve;
          resolve = null;
          r({ value: undefined, done: true });
        }
        done = true;
        completionCallbacks.delete(onComplete);
        unsub();
        cleanupAbortListener();
        return Promise.resolve({
          value: undefined,
          done: true
        });
      }
    };
    return iterator;
  }
  const target = {
    addEventListener,
    removeEventListener,
    dispatchEvent,
    clear,
    on,
    once: onceMethod,
    removeAllListeners,
    pipe,
    addWildcardListener,
    removeWildcardListener,
    complete: complete2,
    get completed() {
      return isCompleted;
    },
    subscribe,
    toObservable,
    events
  };
  target[SymbolObservable] = () => {
    return toObservable();
  };
  return target;
}

class EventEmission {
  #target;
  constructor() {
    this.#target = createEventTarget();
  }
  addEventListener(type, listener, options) {
    return this.#target.addEventListener(type, listener, options);
  }
  removeEventListener(type, listener, options) {
    this.#target.removeEventListener(type, listener, options);
  }
  dispatchEvent(event) {
    return this.#target.dispatchEvent(event);
  }
  on(type, options) {
    return this.#target.on(type, options);
  }
  once(type, listener, options) {
    return this.#target.once(type, listener, options);
  }
  removeAllListeners(type) {
    this.#target.removeAllListeners(type);
  }
  clear() {
    this.#target.clear();
  }
  pipe(target, mapFn) {
    return this.#target.pipe(target, mapFn);
  }
  addWildcardListener(pattern, listener, options) {
    return this.#target.addWildcardListener(pattern, listener, options);
  }
  removeWildcardListener(pattern, listener) {
    this.#target.removeWildcardListener(pattern, listener);
  }
  subscribe(typeOrObserver, observerOrNext, error2, completeHandler) {
    if (typeof typeOrObserver === "string") {
      return this.#target.subscribe(typeOrObserver, observerOrNext, error2, completeHandler);
    }
    if (typeof typeOrObserver === "function") {
      return this.#target.toObservable().subscribe(typeOrObserver);
    }
    if (typeof typeOrObserver === "object" && typeOrObserver !== null) {
      const maybeObserver = typeOrObserver;
      if (typeof maybeObserver.next === "function" || typeof maybeObserver.error === "function" || typeof maybeObserver.complete === "function") {
        return this.#target.toObservable().subscribe(typeOrObserver);
      }
      return this.#target.toObservable().subscribe({});
    }
    throw new Error("subscribe() requires a string event type, callback function, or observer object");
  }
  toObservable() {
    return this.#target.toObservable();
  }
  [SymbolObservable]() {
    return this.toObservable();
  }
  complete() {
    this.#target.complete();
  }
  get completed() {
    return this.#target.completed;
  }
  events(type, options) {
    return this.#target.events(type, options);
  }
}

// src/history.ts
class ConversationHistory extends EventTarget {
  currentNode;
  environment;
  events;
  constructor(initial = createConversation(), environment) {
    super();
    this.environment = resolveConversationEnvironment(environment);
    this.events = createEventTarget();
    const safeInitial = ensureConversationSafe(initial);
    this.currentNode = {
      conversation: safeInitial,
      parent: null,
      children: []
    };
  }
  notifyChange(type) {
    const detail = {
      type,
      conversation: this.current
    };
    this.events.dispatchEvent({ type: "change", detail });
    this.events.dispatchEvent({ type, detail });
  }
  toAddListenerOptions(options) {
    if (typeof options === "boolean" || options === undefined)
      return options;
    const mapped = {};
    if (options.capture !== undefined)
      mapped.capture = options.capture;
    if (options.once !== undefined)
      mapped.once = options.once;
    if (options.passive !== undefined)
      mapped.passive = options.passive;
    if (options.signal !== undefined) {
      mapped.signal = options.signal;
    }
    return mapped;
  }
  toRemoveListenerOptions(options) {
    if (typeof options === "boolean" || options === undefined)
      return options;
    const mapped = {};
    if (options.capture !== undefined)
      mapped.capture = options.capture;
    return mapped;
  }
  addEventListener(type, callback, options) {
    if (!callback)
      return;
    return this.events.addEventListener(type, callback, this.toAddListenerOptions(options));
  }
  removeEventListener(type, callback, options) {
    if (!callback)
      return;
    this.events.removeEventListener(type, callback, this.toRemoveListenerOptions(options));
  }
  dispatchEvent(event) {
    return this.events.dispatchEvent(event);
  }
  subscribe(run) {
    run(this.current);
    const handler = (event) => {
      if (event?.detail?.conversation) {
        run(event.detail.conversation);
      }
    };
    const unsubscribe2 = this.addEventListener("change", handler);
    return unsubscribe2 || (() => {});
  }
  getSnapshot() {
    return this.current;
  }
  get current() {
    return this.currentNode.conversation;
  }
  get ids() {
    return getMessageIds(this.current);
  }
  get canUndo() {
    return this.currentNode.parent !== null;
  }
  get canRedo() {
    return this.currentNode.children.length > 0;
  }
  get env() {
    return this.environment;
  }
  get branchCount() {
    return this.currentNode.parent ? this.currentNode.parent.children.length : 1;
  }
  get branchIndex() {
    if (!this.currentNode.parent)
      return 0;
    return this.currentNode.parent.children.indexOf(this.currentNode);
  }
  get redoCount() {
    return this.currentNode.children.length;
  }
  push(next2) {
    const newNode = {
      conversation: next2,
      parent: this.currentNode,
      children: []
    };
    this.currentNode.children.push(newNode);
    this.currentNode = newNode;
    this.notifyChange("push");
  }
  undo() {
    if (this.currentNode.parent) {
      this.currentNode = this.currentNode.parent;
      this.notifyChange("undo");
      return this.current;
    }
    return;
  }
  redo(childIndex = 0) {
    const next2 = this.currentNode.children[childIndex];
    if (next2) {
      this.currentNode = next2;
      this.notifyChange("redo");
      return this.current;
    }
    return;
  }
  switchToBranch(index) {
    if (this.currentNode.parent) {
      const target = this.currentNode.parent.children[index];
      if (target) {
        this.currentNode = target;
        this.notifyChange("switch");
        return this.current;
      }
    }
    return;
  }
  getPath() {
    const path = [];
    let curr = this.currentNode;
    while (curr) {
      path.unshift(curr.conversation);
      curr = curr.parent;
    }
    return path;
  }
  getMessages(options) {
    return getMessages(this.current, options);
  }
  getMessageAtPosition(position) {
    return getMessageAtPosition(this.current, position);
  }
  getMessageIds() {
    return getMessageIds(this.current);
  }
  getMessageById(id) {
    return getMessageById(this.current, id);
  }
  get(id) {
    return getMessageById(this.current, id);
  }
  searchMessages(predicate) {
    return searchConversationMessages(this.current, predicate);
  }
  getStatistics() {
    return getStatistics(this.current);
  }
  hasSystemMessage() {
    return hasSystemMessage(this.current);
  }
  getFirstSystemMessage() {
    return getFirstSystemMessage(this.current);
  }
  getSystemMessages() {
    return getSystemMessages(this.current);
  }
  toChatMessages() {
    return toChatMessages(this.current);
  }
  estimateTokens(estimator) {
    return estimateConversationTokens(this.current, estimator, this.env);
  }
  getRecentMessages(count, options) {
    return getRecentMessages(this.current, count, options);
  }
  getStreamingMessage() {
    return getStreamingMessage(this.current);
  }
  appendMessages(...inputs) {
    this.push(appendMessages(this.current, ...inputs, this.env));
  }
  appendUserMessage(content, metadata) {
    this.push(appendUserMessage(this.current, content, metadata, this.env));
  }
  appendAssistantMessage(content, metadata) {
    this.push(appendAssistantMessage(this.current, content, metadata, this.env));
  }
  appendSystemMessage(content, metadata) {
    this.push(appendSystemMessage(this.current, content, metadata, this.env));
  }
  prependSystemMessage(content, metadata) {
    this.push(prependSystemMessage(this.current, content, metadata, this.env));
  }
  replaceSystemMessage(content, metadata) {
    this.push(replaceSystemMessage(this.current, content, metadata, this.env));
  }
  collapseSystemMessages() {
    this.push(collapseSystemMessages(this.current, this.env));
  }
  redactMessageAtPosition(position, placeholderOrOptions) {
    this.push(redactMessageAtPosition(this.current, position, placeholderOrOptions, this.env));
  }
  truncateFromPosition(position, options) {
    this.push(truncateFromPosition(this.current, position, options, this.env));
  }
  truncateToTokenLimit(maxTokens, options) {
    this.push(truncateToTokenLimit(this.current, maxTokens, options, this.env));
  }
  appendStreamingMessage(role, metadata) {
    const { conversation, messageId } = appendStreamingMessage(this.current, role, metadata, this.env);
    this.push(conversation);
    return messageId;
  }
  updateStreamingMessage(messageId, content) {
    this.push(updateStreamingMessage(this.current, messageId, content, this.env));
  }
  finalizeStreamingMessage(messageId, options) {
    this.push(finalizeStreamingMessage(this.current, messageId, options, this.env));
  }
  cancelStreamingMessage(messageId) {
    this.push(cancelStreamingMessage(this.current, messageId, this.env));
  }
  snapshot() {
    const getPath = (node) => {
      const path = [];
      let curr = node;
      while (curr.parent) {
        path.unshift(curr.parent.children.indexOf(curr));
        curr = curr.parent;
      }
      return path;
    };
    const serializeNode = (node) => ({
      conversation: node.conversation,
      children: node.children.map(serializeNode)
    });
    let root = this.currentNode;
    while (root.parent) {
      root = root.parent;
    }
    return {
      root: serializeNode(root),
      currentPath: getPath(this.currentNode)
    };
  }
  static from(json, environment) {
    const rootConv = deserializeConversation(json.root.conversation);
    const history = new ConversationHistory(rootConv, environment);
    const buildTree = (nodeJSON, parentNode) => {
      const nodeConv = deserializeConversation(nodeJSON.conversation);
      const node = {
        conversation: nodeConv,
        parent: parentNode,
        children: []
      };
      node.children = nodeJSON.children.map((child) => buildTree(child, node));
      return node;
    };
    const h = history;
    const rootNode = h.currentNode;
    rootNode.children = json.root.children.map((child) => buildTree(child, rootNode));
    let current = rootNode;
    for (const index of json.currentPath) {
      const target = current.children[index];
      if (target) {
        current = target;
      }
    }
    h.currentNode = current;
    return history;
  }
  bind(fn) {
    return (...args) => {
      const boundFn = fn;
      const result = boundFn(this.current, ...args, this.env);
      if (isConversation2(result)) {
        this.push(result);
      }
      return result;
    };
  }
  [Symbol.dispose]() {
    let root = this.currentNode;
    while (root?.parent) {
      root = root.parent;
    }
    const clearNode = (node) => {
      for (const child of node.children) {
        clearNode(child);
      }
      node.children = [];
      const n = node;
      n.parent = null;
      n.conversation = null;
    };
    if (root)
      clearNode(root);
    this.events.clear();
  }
}
function isConversation2(value) {
  return value !== null && typeof value === "object" && typeof value.schemaVersion === "number" && typeof value.id === "string" && typeof value.status === "string" && value.metadata !== null && typeof value.metadata === "object" && Array.isArray(value.ids) && typeof value.messages === "object" && value.messages !== null && !Array.isArray(value.messages) && typeof value.createdAt === "string" && typeof value.updatedAt === "string";
}
export {
  withEnvironment,
  withConversation,
  validateConversationIntegrity,
  updateStreamingMessage,
  truncateToTokenLimit,
  truncateFromPosition,
  toolResultSchema,
  toolCallSchema,
  tokenUsageSchema,
  toMultiModalArray,
  toChatMessages,
  stripTransientMetadata,
  stripTransientFromRecord,
  simpleTokenEstimator,
  searchConversationMessages,
  replaceSystemMessage,
  redactMessageAtPosition,
  prependSystemMessage,
  pipeConversation,
  pairToolCallsWithResults,
  normalizeContent,
  multiModalContentSchema,
  messageSchema,
  messageRoleSchema,
  messageInputSchema,
  jsonValueSchema,
  isTransientKey,
  isToolResult,
  isToolCall,
  isTokenUsage,
  isStreamingMessage,
  isMultiModalContent,
  isMessageRole,
  isMessageInput,
  isMessage,
  isJSONValue,
  isConversationStatus,
  isConversation,
  isAssistantMessage,
  hasSystemMessage,
  getToolInteractions,
  getSystemMessages,
  getStreamingMessage,
  getStatistics,
  getRecentMessages,
  getPendingToolCalls,
  getMessages,
  getMessageIds,
  getMessageById,
  getMessageAtPosition,
  getFirstSystemMessage,
  finalizeStreamingMessage,
  estimateConversationTokens,
  deserializeConversation,
  createValidationError,
  createSerializationError,
  createNotFoundError,
  createMessage,
  createLockedError,
  createInvalidToolReferenceError,
  createInvalidPositionError,
  createInvalidInputError,
  createDuplicateIdError,
  createConversationUnsafe,
  createConversation,
  copyMultiModalContent,
  copyContent,
  conversationShape,
  conversationSchema,
  collapseSystemMessages,
  cancelStreamingMessage,
  assertConversationIntegrity,
  appendUserMessage,
  appendUnsafeMessage,
  appendToolUse,
  appendToolResult,
  appendSystemMessage,
  appendStreamingMessage,
  appendMessages,
  appendAssistantMessage,
  ConversationalistError,
  ConversationHistory
};

//# debugId=F37C8EF457F39BC364756E2164756E21
